import { fetchAuthSession } from 'aws-amplify/auth'
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'

import { env } from '@/lib/env'

/**
 * Custom config flag we set on requests once we've already retried them
 * after a 401. Prevents a refresh-and-retry loop if the second call also
 * returns 401 — at that point the session is genuinely dead and the
 * caller should be signed out.
 */
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

/**
 * The API Gateway's `$default` stage invoke URL has no path component, but
 * every route is registered under `/v1/...` (the team's hand-rolled
 * version prefix). The terraform output exposes the bare invoke URL, so
 * Android adds `/v1/` manually in `local.properties` — we make the web
 * client do that automatically so a fresh `.env.local` populated straight
 * from `terraform output` Just Works.
 *
 * If the user has already included `/v1` (or `/v2` in future), we
 * leave their URL alone.
 */
const API_VERSION_PATH = '/v1'

export function normaliseBaseURL(raw: string): string {
  const trimmed = raw.replace(/\/+$/, '')
  if (/\/v\d+$/.test(trimmed)) return trimmed
  return `${trimmed}${API_VERSION_PATH}`
}

/** Optional sign-out hook — wired up by the AuthProvider when it mounts. */
let onSessionInvalid: (() => void) | null = null

/**
 * Register a callback to fire when the API client decides the Cognito
 * session is unrecoverable (refresh failed, or refresh succeeded but a
 * retry still hit 401). The AuthProvider passes its `signOut` here so the
 * app drops back to /login automatically when tokens expire mid-session.
 */
export function registerSessionInvalidator(handler: () => void) {
  onSessionInvalid = handler
}

let cachedClient: AxiosInstance | null = null

/**
 * Returns the shared axios instance. Created lazily so importing this
 * module doesn't trigger env validation at the top of the bundle —
 * landing-only browsing without a populated `.env.local` keeps working.
 */
export function api(): AxiosInstance {
  if (cachedClient) return cachedClient

  // ── Header note ─────────────────────────────────────────────────────
  // Only `content-type` and `authorization` are in the backend's CORS
  // allow_headers list (see AWS_e404 modules/api/api-gateway.tf). Sending
  // X-RGN-Client (mirroring the Android client) trips the browser
  // preflight on every request and makes the SPA appear to hang because
  // the actual request never leaves the browser. Once the API Gateway
  // CORS config is updated to permit x-rgn-client / x-rgn-version, we
  // can re-introduce them here for analytics-tracking parity.
  const instance = axios.create({
    baseURL: normaliseBaseURL(env().VITE_API_BASE_URL),
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // ── Request: attach the freshest Cognito ID token ────────────────────
  // We deliberately use the ID token here (not the access token) because
  // the API Gateway authoriser is configured for the ID token claim set
  // (sub, email, custom:verifiedDomain, etc.). Matches the Android client.
  instance.interceptors.request.use(async (config) => {
    try {
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken?.toString()
      if (idToken) {
        config.headers.set('Authorization', `Bearer ${idToken}`)
      }
    } catch {
      // No session — endpoints that require auth will 401; public endpoints
      // (/status, /games/search, etc.) still work without a header.
    }
    return config
  })

  // ── Response: refresh-and-retry on a single 401 ──────────────────────
  // Amplify keeps refresh tokens in localStorage. A 401 normally means the
  // ID token expired between requests; calling fetchAuthSession again
  // exchanges the refresh token for a new ID token transparently. If the
  // retry still 401s, the session is dead — clear it.
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const original = error.config as RetryableConfig | undefined
      const status = error.response?.status

      if (status !== 401 || !original || original._retried) {
        if (status === 401) {
          onSessionInvalid?.()
        }
        return Promise.reject(error)
      }

      original._retried = true
      try {
        // Force a refresh by calling fetchAuthSession({ forceRefresh: true })
        // — this rotates the ID token without touching local storage state.
        await fetchAuthSession({ forceRefresh: true })
      } catch {
        onSessionInvalid?.()
        return Promise.reject(error)
      }
      return instance(original)
    },
  )

  cachedClient = instance
  return instance
}
